/**
 * JobOffersModal — Section "Offres sauvegardées" (Phase 2+3).
 *
 * Features:
 * - List of saved job offers with score, source, date, favorite flag
 * - Add new offer: URL or paste text → AI parsing → save
 * - Delete offer
 * - Toggle favorite
 * - For each offer: best score, CV versions tested, link to adapt
 */

import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import {
  Star, Plus, Trash2, Loader2, AlertCircle, Globe, ClipboardPaste,
  ChartNoAxesCombined, ChevronRight, Briefcase, Zap, PenLine, CheckCircle2, Sparkles,
} from 'lucide-react';
import { Textarea } from '../../ui/textarea';
import { cn } from '@/lib/utils';
import { Modal } from '../../ui/Modal';
import type { JobOffer, CVxJobEntry } from '../../../hooks/useJobOffers';
import type { TemplateMetaItem } from '../../../hooks/useCvState';
import { computePrescore } from '../../../lib/prescore';

interface JobOffersModalProps {
  open: boolean;
  onClose: () => void;
  jobOffers: JobOffer[];
  cvxjobEntries: CVxJobEntry[];
  templateMeta: TemplateMetaItem[];
  onAddOffer: (offer: Omit<JobOffer, 'id' | 'dateAdded'>) => void;
  onRemoveOffer: (id: string) => void;
  onToggleFavorite: (id: string) => void;
  onSelectOffer: (offer: JobOffer) => void; // triggers Adapt modal with this offer pre-filled
  activeTemplate?: string;
  onAnalyzeOffer?: (offer: JobOffer) => void;
  isAiAnalyzing?: boolean;
  /** Switch active CV to a specific version ID */
  onSwitchToCv?: (id: string) => void;
  /** Compétences du CV actif — pour calcul du prescore local */
  cvSkills?: string[];
}

type AddMode = 'none' | 'paste';
type ParseState = 'idle' | 'loading' | 'done' | 'error';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
}

function SourceBadge({ source, t }: { source: JobOffer['source']; t: (k: string) => string }) {
  if (source === 'extension') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-violet-500/15 text-violet-400 border border-violet-500/20">
        <Zap className="size-2.5" />Extension
      </span>
    );
  }
  if (source === 'paste') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-blue-500/15 text-blue-400 border border-blue-500/20">
        <ClipboardPaste className="size-2.5" />{t('jobOffers.sourcePaste')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-white/5 text-white/40 border border-white/10">
      <PenLine className="size-2.5" />{t('jobOffers.sourceManual')}
    </span>
  );
}

export function JobOffersModal({
  open,
  onClose,
  jobOffers,
  cvxjobEntries,
  templateMeta,
  onAddOffer,
  onRemoveOffer,
  onToggleFavorite,
  onSelectOffer,
  activeTemplate,
  onAnalyzeOffer,
  isAiAnalyzing,
  onSwitchToCv,
  cvSkills = [],
}: JobOffersModalProps) {
  const { t } = useI18n();
  const [addMode, setAddMode] = useState<AddMode>('none');
  const [pasteText, setPasteText] = useState('');
  const [pasteTitle, setPasteTitle] = useState('');
  const [pasteCompany, setPasteCompany] = useState('');
  const [parseState, setParseState] = useState<ParseState>('idle');
  const [parseError, setParseError] = useState<string | null>(null);
  const [analyzingOfferId, setAnalyzingOfferId] = useState<string | null>(null);

  // Clear spinner once AI analysis finishes
  useEffect(() => {
    if (!isAiAnalyzing && analyzingOfferId) {
      setAnalyzingOfferId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAiAnalyzing]);


  const resetAdd = useCallback(() => {
    setAddMode('none');
    setPasteText('');
    setPasteTitle('');
    setPasteCompany('');
    setParseState('idle');
    setParseError(null);
  }, []);

  const handleSaveManual = useCallback(() => {
    if (!pasteTitle.trim() && !pasteText.trim()) return;
    onAddOffer({
      title: pasteTitle.trim() || t('jobOffers.noTitle'),
      company: pasteCompany.trim() || t('jobOffers.noCompany'),
      source: 'paste',
      rawText: pasteText.trim(),
      isFavorite: false,
    });
    resetAdd();
  }, [pasteTitle, pasteCompany, pasteText, onAddOffer, resetAdd, t]);

  const handleParseAndSave = useCallback(async () => {
    if (!pasteText.trim()) return;
    setParseState('loading');
    setParseError(null);
    try {
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ route: 'parse', jobDescription: pasteText.trim() }),
      });
      const json = await res.json() as { data?: { titre?: string; entreprise?: string }; error?: string };
      const parsed = json.data;
      onAddOffer({
        title: pasteTitle.trim() || parsed?.titre || t('jobOffers.noTitle'),
        company: pasteCompany.trim() || parsed?.entreprise || t('jobOffers.noCompany'),
        source: 'paste',
        rawText: pasteText.trim(),
        isFavorite: false,
      });
      setParseState('done');
      setTimeout(resetAdd, 600);
    } catch (err) {
      // Fallback: save without AI parsing
      onAddOffer({
        title: pasteTitle.trim() || t('jobOffers.noTitle'),
        company: pasteCompany.trim() || t('jobOffers.noCompany'),
        source: 'paste',
        rawText: pasteText.trim(),
        isFavorite: false,
      });
      setParseError(err instanceof Error ? err.message : t('jobOffers.parseError'));
      setParseState('error');
      setTimeout(resetAdd, 1500);
    }
  }, [pasteTitle, pasteCompany, pasteText, onAddOffer, resetAdd, t]);

  const getTestedCvCount = useCallback((jobId: string) => {
    return new Set(cvxjobEntries.filter((e) => e.jobOfferId === jobId).map((e) => e.cvVersionId)).size;
  }, [cvxjobEntries]);

  const getScoreForCurrentCv = useCallback((jobId: string) => {
    if (!activeTemplate) return null;
    const entry = cvxjobEntries.find((e) => e.jobOfferId === jobId && e.cvVersionId === activeTemplate && e.atsScore !== undefined);
    return entry?.atsScore ?? null;
  }, [cvxjobEntries, activeTemplate]);

  /** Meilleur score d'un CV différent du CV actif, avec l'ID et le nom du CV */
  const getBestOtherCvEntry = useCallback((jobId: string): { score: number; cvVersionId: string; cvName: string | null } | null => {
    let best: { score: number; cvVersionId: string } | null = null;
    for (const e of cvxjobEntries) {
      if (e.cvVersionId !== activeTemplate && e.jobOfferId === jobId && e.atsScore !== undefined) {
        if (best === null || (e.atsScore ?? 0) > best.score) {
          best = { score: e.atsScore as number, cvVersionId: e.cvVersionId };
        }
      }
    }
    if (!best) return null;
    const cvName = templateMeta.find((t) => t.id === best!.cvVersionId)?.name ?? null;
    return { ...best, cvName };
  }, [cvxjobEntries, activeTemplate, templateMeta]);

  // Sorted: favorites first, then by date
  const sorted = [...jobOffers].sort((a, b) => {
    if (a.isFavorite && !b.isFavorite) return -1;
    if (!a.isFavorite && b.isFavorite) return 1;
    return new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime();
  });

  const modalTitle = (
    <span className="flex items-center gap-2">
      <Briefcase className="size-4 text-blue-400 shrink-0" />
      {t('jobOffers.title')}
      {jobOffers.length > 0 && (
        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-500/20 text-blue-300">
          {jobOffers.length}
        </span>
      )}
    </span>
  );

  return (
    <Modal open={open} onClose={onClose} title={modalTitle} size="lg" bodyClassName="p-0">
      {/* Section ajout d'offre */}
      {addMode === 'none' ? (
        <div className="px-5 py-3 border-b border-[var(--modal-border)]">
          <button
            type="button"
            onClick={() => setAddMode('paste')}
            className="flex items-center gap-2 w-full px-4 py-2.5 rounded-xl border border-dashed border-blue-500/30 text-blue-400 hover:bg-blue-500/10 transition-colors text-sm font-medium"
          >
            <Plus className="size-4" />
            {t('jobOffers.add')}
          </button>
        </div>
      ) : (
        <div className="px-5 py-4 border-b border-[var(--modal-border)] space-y-3 bg-blue-500/5">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-[var(--modal-muted)] flex items-center gap-2">
              <ClipboardPaste className="size-4" /> {t('jobOffers.newOffer')}
            </p>
            <button type="button" onClick={resetAdd} className="text-xs text-[var(--modal-muted)] hover:opacity-80">{t('common.cancel')}</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              type="text"
              value={pasteTitle}
              onChange={(e) => setPasteTitle(e.target.value)}
              placeholder={t('jobOffers.titlePlaceholder')}
              className="px-3 py-2 rounded-lg bg-black/10 border border-[var(--modal-border)] text-sm text-[var(--modal-fg)] placeholder:text-[var(--modal-muted)] focus:outline-none focus:border-blue-500/50"
            />
            <input
              type="text"
              value={pasteCompany}
              onChange={(e) => setPasteCompany(e.target.value)}
              placeholder={t('jobOffers.companyPlaceholder')}
              className="px-3 py-2 rounded-lg bg-black/10 border border-[var(--modal-border)] text-sm text-[var(--modal-fg)] placeholder:text-[var(--modal-muted)] focus:outline-none focus:border-blue-500/50"
            />
          </div>
          <Textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={t('jobOffers.textPlaceholder')}
            rows={4}
            className="resize-none bg-black/10 border-[var(--modal-border)] text-[var(--modal-fg)] placeholder:text-[var(--modal-muted)] text-sm"
          />
          {parseError && (
            <p className="flex items-center gap-1.5 text-xs text-amber-400">
              <AlertCircle className="size-3.5" /> {parseError}
            </p>
          )}
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={handleSaveManual}
              disabled={!pasteTitle.trim() && !pasteText.trim()}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-black/10 hover:bg-black/20 text-[var(--modal-muted)] disabled:opacity-40 disabled:pointer-events-none"
            >
              {t('jobOffers.saveWithoutParsing')}
            </button>
            <button
              type="button"
              onClick={handleParseAndSave}
              disabled={!pasteText.trim() || parseState === 'loading'}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40 disabled:pointer-events-none"
            >
              {parseState === 'loading' ? (
                <><Loader2 className="size-3 animate-spin" /> {t('jobOffers.analyzingShort')}</>
              ) : (
                <><Globe className="size-3" /> {t('jobOffers.analyzeAndSave')}</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Liste des offres */}
      {sorted.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-[var(--modal-muted)]">
          <Briefcase className="size-10 opacity-40" />
          <p className="text-sm">{t('jobOffers.empty')}</p>
          <p className="text-xs text-center max-w-xs opacity-70">{t('jobOffers.emptyHint')}</p>
        </div>
      ) : (
        <div className="divide-y divide-[var(--modal-border)]">
          {sorted.map((offer) => {
            const prescore = computePrescore(cvSkills, offer.parsedData);
            const currentScore = getScoreForCurrentCv(offer.id);
            const otherCvEntry = currentScore === null ? getBestOtherCvEntry(offer.id) : null;
            const testedCount = getTestedCvCount(offer.id);
            const isThisAnalyzing = analyzingOfferId === offer.id && isAiAnalyzing;

            return (
              <div key={offer.id} className="px-5 py-3 hover:bg-black/5 transition-colors">
                {/* Ligne 1 : étoile + titre + supprimer */}
                <div className="flex items-start gap-2.5">
                  <button
                    type="button"
                    onClick={() => onToggleFavorite(offer.id)}
                    className={cn('mt-0.5 shrink-0 transition-colors', offer.isFavorite ? 'text-amber-400' : 'text-[var(--modal-muted)] opacity-30 hover:opacity-70 hover:text-amber-400')}
                  >
                    <Star className="size-3.5" fill={offer.isFavorite ? 'currentColor' : 'none'} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm leading-snug truncate text-[var(--modal-fg)]">{offer.title}</div>
                    <div className="text-[11px] text-[var(--modal-muted)] flex items-center gap-1.5 flex-wrap mt-0.5">
                      {offer.company && <span className="truncate max-w-[140px]">{offer.company}</span>}
                      {offer.company && <span className="opacity-30">·</span>}
                      <SourceBadge source={offer.source} t={t} />
                      <span className="opacity-30">·</span>
                      <span>{formatDate(offer.dateAdded)}</span>
                      {testedCount > 1 && <span className="opacity-40">· {testedCount} CV</span>}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemoveOffer(offer.id)}
                    className="shrink-0 p-1 rounded-lg text-[var(--modal-muted)] opacity-30 hover:opacity-100 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    title={t('jobOffers.delete')}
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>

                {/* Ligne 2 : scores + actions inline */}
                <div className="flex items-center gap-1.5 flex-wrap mt-2 pl-5">
                  {prescore !== null && (
                    <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
                      prescore >= 60
                        ? 'text-teal-500 bg-teal-500/10 border-teal-500/20'
                        : prescore >= 35
                        ? 'text-[var(--modal-muted)] bg-black/5 border-[var(--modal-border)]'
                        : 'text-[var(--modal-muted)] opacity-50 bg-black/5 border-[var(--modal-border)]'
                    }`}>
                      ~{prescore}%
                    </span>
                  )}
                  {currentScore !== null && (
                    <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border ${
                      currentScore >= 70
                        ? 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20'
                        : currentScore >= 50
                        ? 'text-amber-500 bg-amber-500/10 border-amber-500/20'
                        : 'text-red-500 bg-red-500/10 border-red-500/20'
                    }`}>
                      <CheckCircle2 className="size-2.5" />
                      {currentScore}/100
                    </span>
                  )}
                  {currentScore === null && otherCvEntry !== null && (
                    <button
                      type="button"
                      onClick={() => onSwitchToCv?.(otherCvEntry.cvVersionId)}
                      className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border text-blue-500 bg-blue-500/10 border-blue-500/20 hover:bg-blue-500/20 transition-colors"
                    >
                      <Sparkles className="size-2.5" />
                      {otherCvEntry.score}/100
                      <ChevronRight className="size-2.5 opacity-60" />
                    </button>
                  )}

                  <span className="text-[var(--modal-border)] select-none">|</span>

                  {currentScore === null && onAnalyzeOffer && (
                    <button
                      type="button"
                      disabled={isThisAnalyzing || (isAiAnalyzing ?? false)}
                      onClick={() => { setAnalyzingOfferId(offer.id); onAnalyzeOffer(offer); }}
                      className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-violet-500/15 text-violet-500 hover:bg-violet-500/25 transition-colors disabled:opacity-40"
                    >
                      {isThisAnalyzing ? <Loader2 className="size-2.5 animate-spin" /> : <ChartNoAxesCombined className="size-2.5" />}
                      {isThisAnalyzing ? t('jobOffers.aiAnalyzing') : t('jobOffers.aiAnalysis')}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onSelectOffer(offer)}
                    className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-500/15 text-blue-500 hover:bg-blue-500/25 transition-colors"
                  >
                    {t('jobOffers.adapt')} <ChevronRight className="size-2.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
